/* eslint-disable no-plusplus, no-param-reassign, no-underscore-dangle, no-restricted-syntax, no-await-in-loop */
import * as turf from '@turf/turf';

import { MAPBOX_SOURCECACHE_LAYER_PREFIX } from '../../../constants/map';
import { resizeMapContainer } from '../../map';
import {
  clearBuffer,
  ensureTilesLoad,
  getClippableTilesetLayers,
  hideLayersExcept,
  getMapboxTileClass,
  getCalculateSuitabilityFromMap,
} from './helpers';

/**
 * We resize the map container to 512x512 before running suitability
 * calculations. We do this for consistency and performance. The map renders
 * slightly differently for different dimensions, which would lead to slightly
 * different numbers for suitable acres (so resizing your browser would change
 * the results a bit). Standardizing the map to always be 512x512 removes that
 * issue. And using 512x512 that instead of 1000x1000 or something else is
 * mostly just for performance. It takes longer to pull more pixels, so smaller
 * is better, but too small would hurt accuracy, so 512x512 is a pretty good
 * compromise.
 */
const MAP_CONTAINER_SIZE = 512;

/**
 * Load all of the tiles we'll need for the bounding boxes of the given
 * `features`, across all `layerIds`. Reports progress via `onProgress` every
 * `progressFreq` tiles.
 */
const preloadTiles = async ({
  map,
  features,
  layerIds,
  onProgress,
  progressFreq,
}) => {
  if (!map || !features?.length || !features?.length) return;

  const { painter } = map;

  const sourceCaches = layerIds
    .map((layerId) => {
      const layer = painter.style._layers[layerId];
      if (!layer) return null;

      const sourceCacheKey = `${MAPBOX_SOURCECACHE_LAYER_PREFIX}${layer.source}`;
      return painter.style._sourceCaches[sourceCacheKey];
    })
    .filter(Boolean);

  if (!sourceCaches.length) {
    throw new Error('No source caches or features to preload.');
  }

  // Calculate all transforms
  const transforms = features.map((feature) => {
    const { center, bearing, pitch, zoom } = map.cameraForBounds(
      turf.bbox(feature),
      {
        padding: 10,
        animate: false,
      },
    );
    const transform = painter.transform.clone();
    transform.center = center;
    transform.bearing = bearing;
    transform.pitch = pitch;
    transform.zoom = zoom;
    return transform;
  });

  const tileIdMap = {}; // We'll use this to store unique tiles
  const tileSize = 256; // NOTE: if we change our tile size, this needs to be updated
  const zoom = 12; // NOTE: if we start using tilesets that don't have zoom level 12, this needs to be updated

  transforms.forEach((tr) => {
    tr.coveringTiles({
      tileSize,
      minzoom: zoom,
      maxzoom: zoom,
    }).forEach((tileID) => {
      tileIdMap[tileID.key] = [tileID, tr];
    });
  });
  const tileIDs = Object.values(tileIdMap);

  const totalTiles = tileIDs.length * sourceCaches.length;

  // Show initial progress for tile preloading phase:
  onProgress('preloading', 0, totalTiles);

  // Patch the source caches to ensure they don't remove tiles when the cache gets too big
  sourceCaches.forEach((sourceCache) => {
    if (!sourceCache.__HOT_PATCHED__) {
      sourceCache.__HOT_PATCHED__ = true;
      sourceCache._maxTileCacheSize = 999999;
      sourceCache._cache.max = 999999;
      sourceCache._cache.setMaxSize = () => {};
    }
  });

  /**
   * Loads `tile` and stores it in the tile cache of `sourceCache`
   */
  const loadTile = ([tile, sourceCache]) =>
    new Promise((resolve, reject) => {
      const tileIsAlreadyLoaded = sourceCache._cache.has(tile.tileID);
      if (tileIsAlreadyLoaded) {
        resolve();
        return;
      }

      sourceCache._loadTile(tile, (err) => {
        if (err) reject(err);
        else {
          sourceCache._cache.add(tile.tileID, tile, tile.getExpiryTimeout());
          resolve();
        }
      });
    });

  const Tile = getMapboxTileClass(map);

  // Generate an array of the tile/sourceCache combos
  const combos = [];
  sourceCaches.forEach((sourceCache) => {
    const tiles = tileIDs.map(
      ([tileID, tr]) =>
        /**
         * NOTE: it's important to create a new tile for each source cache. If
         * we reuse the same tile for different source caches, things break.
         */
        new Tile(
          tileID,
          tileSize * tileID.overscaleFactor(),
          tr.tileZoom,
          map.painter,
          true, // (isRaster)
        ),
    );
    tiles.forEach((tile) => {
      combos.push([tile, sourceCache]);
    });
  });

  // Preload all tiles, updating progress as we go
  let i = 0;
  await Promise.all(
    combos.map(async (combo) => {
      try {
        await loadTile(combo);
      } catch (err) {
        console.error('Failed to load tile:', err);
      }

      i++;
      if (i % progressFreq === 0) {
        onProgress('preloading', i, totalTiles);
      }
    }),
  );

  onProgress('preloading', i, totalTiles);
};

/**
 * An extension of `suitabilityAcrossLayers` that computes suitability across
 * multiple feature collections and multiple layers.
 *
 * This computes the suitability of each feature collection in
 * `featureCollections` across each layer in `layerIds`. Reports progress via
 * `onProgress` every `progressFreq` items.
 *
 * WARNING: this function uses internal Mapbox GL JS APIs.
 */
const suitabilityAcrossLayersAndRegions = async ({
  map,
  layerIds,
  featureCollections,
  onProgress = () => {},
  progressFreq,
}) => {
  if (!map || !layerIds?.length || !featureCollections?.length) {
    throw new Error('Invalid arguments');
  }

  // Standardize the sizing of the map so that the user's viewport size doesn't cause small changes in the results
  const { restoreMapContainer } = resizeMapContainer(map, {
    width: MAP_CONTAINER_SIZE,
    height: MAP_CONTAINER_SIZE,
  });

  const { painter } = map;
  const { gl } = painter.context;

  // Store init values so we can restore them at the end:
  const originalPosition = {
    center: map.getCenter(),
    zoom: map.getZoom(),
    pitch: map.getPitch(),
  };

  // Get the map layers we'll be working with
  const layers = getClippableTilesetLayers(map, layerIds);

  // Save initial layer configs
  const originalUniformValues = Object.fromEntries(
    layers.map((layer) => [
      layer.implementation.id,
      {
        opacity: layer.implementation.opacity,
        fadeFactor: layer.implementation.fadeFactor,
        maskPolygons: layer.implementation.maskPolygons,
      },
    ]),
  );

  // Total number of items (for tracking progress)
  const totalItems = featureCollections.length * layers.length;

  let i = 0; // Counter for tracking progress

  /**
   * Computes the suitability for the given layer and set of polygons, at the
   * current map `transform`.
   */
  const suitabilityForLayer = async ({
    layer,
    transform,
    featureCollection,
    calculateSuitabilityFromMap,
  }) => {
    // Compute the matrix w/ the map in the current position
    // We need to pass this to the `RasterLayerWithClipping.render` method
    const matrix = transform.customLayerMatrix();

    // Get the RasterLayerWithClipping instance
    const customLayer = layer.implementation;

    // Update the layer mask to only show the current set of polygons
    customLayer.updateMask(featureCollection.features);

    // Ensure the tiles for this map position are loaded for this render
    await ensureTilesLoad(map, transform);

    // Clear the buffer and render
    clearBuffer(gl);
    customLayer.render(gl, matrix);

    // Calculate the suitable acres
    const result = calculateSuitabilityFromMap();

    // Periodically, update the progress
    i++;
    if (i % progressFreq === 0) {
      onProgress('calculating', i, totalItems);
    }

    return result;
  };

  let restoreLayersVisibility;
  try {
    // Preload all the tiles we'll need up front
    await preloadTiles({
      map,
      features: featureCollections.flatMap((fc) => fc.features),
      layerIds,
      onProgress,
      progressFreq,
    });

    // Show initial progress for calculation phase:
    onProgress('calculating', 0, totalItems);

    // Hide other layers to avoid additional network requests being made as we travel around the map
    ({ restoreLayersVisibility } = hideLayersExcept(layerIds, map));

    // Update layer configs so that the layers are fully opaque and the filtered out colors are fully transparent
    layers.forEach((layer) => {
      layer.implementation.updateOpacity(1);
      layer.implementation.updateFadeFactor(1);
    });

    // For each feature collection, compute the suitability for each layer
    const suitabilityValues = [];
    for (const featureCollection of featureCollections) {
      // Adjust map center/zoom to fit the features
      map.fitBounds(turf.bbox(featureCollection), {
        padding: 10, // minimal padding needed
        animate: false,
      });

      const calculateSuitabilityFromMap = getCalculateSuitabilityFromMap({
        map,
        featureCollection,
        maxPoints: 10_000, // this function doesn't run on mobile, so the points limit can be higher than the default of 2k
      });

      // For each layer, compute the suitability for the current map transform
      const suitabilityByLayerId = {};
      for (const layer of layers) {
        suitabilityByLayerId[layer.id] = await suitabilityForLayer({
          layer,
          transform: painter.transform,
          featureCollection,
          calculateSuitabilityFromMap,
        });
      }
      suitabilityValues.push(suitabilityByLayerId);
    }
    return suitabilityValues;
  } catch (err) {
    console.error(err);
    return null;
  } finally {
    // Restore the map container dimensions
    restoreMapContainer();

    // Restore the layer visibility to original state
    if (restoreLayersVisibility) restoreLayersVisibility();

    // Reset the map position
    map.setCenter(originalPosition.center, { animate: false });
    map.setZoom(originalPosition.zoom, { animate: false });
    map.setPitch(originalPosition.pitch, { animate: false });

    // Reset the layer properties
    layers.forEach((layer) => {
      const { opacity, fadeFactor, maskPolygons } = originalUniformValues[
        layer.id
      ];
      layer.implementation.updateOpacity(opacity);
      layer.implementation.updateFadeFactor(fadeFactor);
      layer.implementation.updateMask(maskPolygons);
    });

    // Make sure the map displays the restored layers
    map.triggerRepaint();
  }
};

export default suitabilityAcrossLayersAndRegions;
